import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowRight, FaDownload, FaPlay } from 'react-icons/fa';
import appstore1 from '../assets/appstore1.PNG';
import appstore2 from '../assets/appstore2.PNG';
import appstore3 from '../assets/appstore3.PNG';
import appstore4 from '../assets/appstore4.PNG';

const BRAND_BLUE = '#0066cc';

const CARDS = [
  { key: 'experience', img: appstore2, tagline: 'CollectedAF', label: 'Experience' },
  { key: 'community',  img: appstore3, tagline: 'Traction &',  label: 'Community' },
  { key: 'technology', img: appstore4, tagline: 'The Tech',    label: 'Technology' },
  { key: 'venues',     img: appstore1, tagline: 'Venues &',    label: 'Business' },
];

const DESKTOP_TILT = [-18, -6, 6, 18];

const RING_COLORS = [
  'conic-gradient(from 0deg, #3b82f6, #fbbf24, #f97316, #3b82f6)',
  'conic-gradient(from 90deg, #14b8a6, #6366f1, #fbbf24, #14b8a6)',
  'conic-gradient(from 180deg, #f97316, #0ea5e9, #6366f1, #f97316)',
  'conic-gradient(from 270deg, #fbbf24, #14b8a6, #3b82f6, #fbbf24)',
];

interface HomeSectionProps {
  onDownloadClick?: () => void;
  onNavigateToExperience?: () => void;
  onNavigateToCommunity?: () => void;
  onNavigateToTechnology?: () => void;
  onNavigateToVenues?: () => void;
}

const HomeSection: React.FC<HomeSectionProps> = ({
  onDownloadClick,
  onNavigateToExperience,
  onNavigateToCommunity,
  onNavigateToTechnology,
  onNavigateToVenues,
}) => {
  const navMap: Record<string, (() => void) | undefined> = {
    experience: onNavigateToExperience,
    community: onNavigateToCommunity,
    technology: onNavigateToTechnology,
    venues: onNavigateToVenues,
  };

  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const stageRef = useRef<HTMLDivElement>(null);
  const pointerY = useRef(0.5);
  const pointerActive = useRef(false);

  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const threshold = 50;
    if (touchDeltaX.current < -threshold) {
      setActiveSlide((prev) => Math.min(prev + 1, CARDS.length - 1));
    } else if (touchDeltaX.current > threshold) {
      setActiveSlide((prev) => Math.max(prev - 1, 0));
    }
    touchDeltaX.current = 0;
  }, []);

  const renderDesktopCard = (card: typeof CARDS[0], i: number) => {
    const isHover = hoveredIdx === i;
    return (
      <button
        key={card.key}
        type="button"
        onClick={() => navMap[card.key]?.()}
        onMouseEnter={() => setHoveredIdx(i)}
        onMouseLeave={() => setHoveredIdx(null)}
        className="relative flex-1 min-w-0 cursor-pointer bg-transparent border-0 p-0 group"
        style={{
          transformStyle: 'preserve-3d',
          transform: `rotateY(${DESKTOP_TILT[i]}deg) translateZ(${isHover ? 60 : 0}px) translateY(${isHover ? -14 : 0}px)`,
          transition: 'transform 0.4s cubic-bezier(.25,.8,.25,1)',
          zIndex: isHover ? 10 : 4 - Math.abs(i - 1.5),
        }}
      >
        <div className="relative rounded-2xl overflow-hidden" style={{ padding: '2px' }}>
          <div className="absolute inset-0 rounded-2xl" style={{ background: RING_COLORS[i], animation: 'ring-spin 3s linear 1 forwards' }} />
          <div className="absolute -inset-1 rounded-2xl opacity-40 pointer-events-none" style={{ background: RING_COLORS[i], filter: 'blur(12px)', animation: 'ring-spin 3s linear 1 forwards' }} />

          <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '9 / 19' }}>
            <img src={card.img} alt={card.label} draggable={false} className="w-full h-full object-cover object-center" loading="lazy" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.85) 100%)' }} />
            {isHover && <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 75%, rgba(255,69,0,0.2) 0%, transparent 55%)' }} />}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-25 pointer-events-none transition-opacity duration-500" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)' }} />

            {/* Glassmorphism header */}
            <div className="absolute top-0 left-0 right-0 px-3 py-2.5 flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(16px) saturate(180%)', WebkitBackdropFilter: 'blur(16px) saturate(180%)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-WHITE/15 text-WHITE text-[10px] font-bold border border-WHITE/20">{i + 1}</span>
              <span className="text-WHITE font-bold text-xs tracking-wide truncate">{card.tagline} {card.label}</span>
            </div>

            {/* Bottom label */}
            <div className="absolute bottom-0 left-0 right-0 p-3 flex flex-col items-start gap-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold text-WHITE/95 backdrop-blur-xl border border-WHITE/10" style={{ background: 'rgba(0,0,0,0.5)' }}>{card.tagline}</span>
              <span className="text-WHITE font-bold text-sm drop-shadow-lg">{card.label}</span>
            </div>
          </div>
        </div>
        {isHover && <div className="absolute -bottom-3 left-[8%] right-[8%] h-8 rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(0,0,0,0.4) 0%, transparent 70%)', filter: 'blur(8px)' }} />}
      </button>
    );
  };

  const renderMobileCard = (card: typeof CARDS[0], i: number) => {
    const isActive = activeSlide === i;
    return (
      <div key={card.key} className="flex-shrink-0 w-full flex items-center justify-center px-4">
        <button
          type="button"
          onClick={() => navMap[card.key]?.()}
          className="relative w-full cursor-pointer bg-transparent border-0 p-0 group active:scale-[0.98] transition-transform"
          style={{ maxWidth: '400px' }}
        >
          <div className="relative rounded-2xl overflow-hidden" style={{ padding: '2px' }}>
            <div className="absolute inset-0 rounded-2xl" style={{ background: RING_COLORS[i], animation: 'ring-spin 3s linear 1 forwards' }} />
            <div className="absolute -inset-1 rounded-2xl opacity-40 pointer-events-none" style={{ background: RING_COLORS[i], filter: 'blur(12px)', animation: 'ring-spin 3s linear 1 forwards' }} />

            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '9 / 16' }}>
              <img src={card.img} alt={card.label} draggable={false} className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.85) 100%)' }} />
              {isActive && <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 75%, rgba(255,69,0,0.15) 0%, transparent 55%)' }} />}

              {/* Glassmorphism header */}
              <div className="absolute top-0 left-0 right-0 px-4 py-3 flex items-center gap-2.5" style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(16px) saturate(180%)', WebkitBackdropFilter: 'blur(16px) saturate(180%)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-WHITE/15 text-WHITE text-xs font-bold border border-WHITE/20">{i + 1}</span>
                <span className="text-WHITE font-bold text-sm tracking-wide">{card.tagline} {card.label}</span>
              </div>

              {/* Bottom CTA overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col items-center gap-3">
                <div>
                  <span className="text-WHITE font-bold text-xl drop-shadow-lg block">{card.label}</span>
                  <span className="text-WHITE/60 text-xs">{card.tagline}</span>
                </div>
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-WHITE backdrop-blur-xl border border-WHITE/20 shadow-lg" style={{ background: 'rgba(255,255,255,0.12)' }}>
                  Explore <FaArrowRight size={12} />
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  };

  return (
    <div className="z-20 flex-shrink-0 py-4 lg:py-6">
      <div className="relative flex flex-col items-center justify-start text-center px-2 lg:px-4 pt-1 pb-3 lg:pt-2 lg:pb-4">

        <span className="inline-block mb-1 px-3 py-1 rounded-full bg-WHITE/10 text-WHITE/80 text-xs font-medium border border-WHITE/20">
          Proximity-powered
        </span>

        <h2
          className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-WHITE mb-1 lg:mb-2 max-w-2xl leading-tight"
          style={{ fontFamily: '"Anton", sans-serif' }}
        >
          Create real connections{' '}
          <span className="relative inline-block whitespace-nowrap">
            <span
              className="absolute inset-0 -skew-x-3 rounded-md"
              style={{ background: BRAND_BLUE, opacity: 0.85, zIndex: -1, top: '10%', bottom: '5%', left: '-4px', right: '-4px' }}
              aria-hidden
            />
            <span className="relative text-WHITE" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
              — in real life
            </span>
          </span>
        </h2>

        <p className="text-WHITE/70 text-xs sm:text-sm mb-1.5 max-w-md">
          See who's around you. Collect people & places. No scrolling — just living.
        </p>

        <div className="flex flex-wrap justify-center gap-2 mb-2 lg:mb-3">
          {['Instant discovery', 'Location-aware', 'Real, not algorithmic'].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-BLACK/60 backdrop-blur-md shadow-lg text-WHITE/90 text-[10px] sm:text-xs font-medium border border-WHITE/10">
              <span className="text-YELLOW">●</span> {t}
            </span>
          ))}
        </div>

        {/* DESKTOP: 3D fanned layout — images fill the space */}
        {!isMobile && (
          <div
            ref={stageRef}
            className="relative w-full mx-auto mb-4"
            style={{ perspective: '1600px' }}
            onPointerMove={(e) => {
              if (!stageRef.current) return;
              const rect = stageRef.current.getBoundingClientRect();
              pointerY.current = (e.clientY - rect.top) / rect.height;
              pointerActive.current = true;
            }}
            onPointerLeave={() => { pointerActive.current = false; }}
          >
            <div
              className="flex flex-row items-end justify-center gap-3 w-full"
              style={{
                transformStyle: 'preserve-3d',
                transform: `rotateX(${pointerActive.current ? (0.5 - pointerY.current) * 6 : 2}deg)`,
                transition: 'transform 0.25s ease-out',
              }}
            >
              {CARDS.map((card, i) => renderDesktopCard(card, i))}
            </div>
            <div className="mx-auto mt-2" style={{ width: '70%', height: '10px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)', borderRadius: '50%', filter: 'blur(4px)' }} />
          </div>
        )}

        {/* MOBILE: Full-width carousel */}
        {isMobile && (
          <div className="w-full mb-4">
            <div
              className="relative overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex"
                style={{
                  transform: `translateX(-${activeSlide * 100}%)`,
                  transition: 'transform 0.4s cubic-bezier(.25,.8,.25,1)',
                }}
              >
                {CARDS.map((card, i) => renderMobileCard(card, i))}
              </div>
            </div>

            {/* Carousel dots */}
            <div className="flex justify-center gap-2 mt-3">
              {CARDS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveSlide(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    activeSlide === i
                      ? 'w-8 bg-WHITE shadow-lg shadow-WHITE/30'
                      : 'w-2 bg-WHITE/30 hover:bg-WHITE/50'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center gap-4" style={{ perspective: '800px' }}>
          {/* Download the App */}
          <button
            type="button"
            onClick={onDownloadClick}
            className="group relative rounded-2xl overflow-hidden transition-all duration-500"
            style={{ transformStyle: 'preserve-3d' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'rotateX(-3deg) rotateY(4deg) scale(1.04)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'rotateX(0) rotateY(0) scale(1)'; }}
          >
            {/* Animated gradient border — green tones */}
            <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 0deg, #22c55e, #4ade80, #86efac, #ffffff, #22c55e)', animation: 'ring-spin 3s linear 1 forwards', padding: '2px' }}>
              <div className="w-full h-full rounded-2xl" style={{ background: 'rgba(22,163,74,0.12)' }} />
            </div>
            {/* Outer glow */}
            <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none" style={{ background: 'conic-gradient(from 0deg, #22c55e, #4ade80, #22c55e)', filter: 'blur(14px)' }} />
            {/* Shimmer sweep */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)', backgroundSize: '200% 100%' }} />
            {/* Content */}
            <div className="relative flex items-center justify-center gap-3 px-7 py-3.5 rounded-2xl backdrop-blur-2xl" style={{ background: 'rgba(0,0,0,0.55)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-[-8deg] border" style={{ background: 'rgba(34,197,94,0.2)', borderColor: 'rgba(34,197,94,0.3)' }}>
                <FaDownload size={13} className="transition-transform duration-500 group-hover:translate-y-[2px]" style={{ color: '#4ade80' }} />
              </div>
              <span className="font-bold text-sm tracking-wide" style={{ color: '#4ade80' }}>Download the App</span>
              <FaArrowRight size={11} className="transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0" style={{ color: 'rgba(255,255,255,0.6)' }} />
            </div>
          </button>

          {/* How it works */}
          <button
            type="button"
            onClick={onNavigateToExperience}
            className="group relative rounded-2xl overflow-hidden transition-all duration-500"
            style={{ transformStyle: 'preserve-3d' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'rotateX(3deg) rotateY(-4deg) scale(1.04)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'rotateX(0) rotateY(0) scale(1)'; }}
          >
            {/* Animated gradient border — blue tones */}
            <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 180deg, #3b82f6, #60a5fa, #93c5fd, #ffffff, #3b82f6)', animation: 'ring-spin 3s linear 1 forwards', padding: '2px' }}>
              <div className="w-full h-full rounded-2xl" style={{ background: 'rgba(59,130,246,0.1)' }} />
            </div>
            {/* Outer glow */}
            <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none" style={{ background: 'conic-gradient(from 180deg, #3b82f6, #60a5fa, #3b82f6)', filter: 'blur(14px)' }} />
            {/* Shimmer sweep */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)', backgroundSize: '200% 100%' }} />
            {/* Content */}
            <div className="relative flex items-center justify-center gap-3 px-7 py-3.5 rounded-2xl backdrop-blur-2xl" style={{ background: 'rgba(0,0,0,0.55)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-[8deg] border" style={{ background: 'rgba(59,130,246,0.2)', borderColor: 'rgba(59,130,246,0.3)' }}>
                <FaPlay size={11} className="transition-transform duration-500 group-hover:scale-110 ml-0.5" style={{ color: '#60a5fa' }} />
              </div>
              <span className="font-bold text-sm tracking-wide" style={{ color: '#60a5fa' }}>How it works</span>
              <FaArrowRight size={11} className="transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0" style={{ color: 'rgba(255,255,255,0.6)' }} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeSection;
